"use client";

import { c as _c } from "react/compiler-runtime";
import type { FC, ReactNode } from "react";
import { useEffect, useRef } from "react";
import { FaTimes } from "react-icons/fa";
import { Button } from "@/components/button";
import type { ComponentPropsWithoutChildren } from "@/lib/component-props";
import { cn } from "@/lib/utils";
export interface ModalProps extends ComponentPropsWithoutChildren<"dialog"> {
  children?: ReactNode;
  open?: boolean;
  onClose?: () => void;
}
export const Modal: FC<ModalProps> = t0 => {
  const $ = _c(16);
  let children;
  let className;
  let onClose;
  let open;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      open,
      onClose,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onClose;
    $[4] = open;
    $[5] = props;
  } else {
    children = $[1];
    className = $[2];
    onClose = $[3];
    open = $[4];
    props = $[5];
  }
  const ref = useRef(null);
  let t1;
  let t2;
  if ($[6] !== open) {
    t1 = () => {
      const dialog = ref.current;
      if (!dialog) {
        return;
      }
      if (open) {
        if (typeof dialog.showModal === "function") {
          dialog.showModal();
        }
      } else {
        if (typeof dialog.close === "function") {
          dialog.close();
        }
      }
    };
    t2 = [open];
    $[6] = open;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  useEffect(t1, t2);
  let t3;
  if ($[9] !== className) {
    t3 = cn("m-auto max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-md border-2 border-black bg-white p-0 text-black shadow-[8px_8px_0_0] backdrop:bg-black/50", className);
    $[9] = className;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== children || $[12] !== onClose || $[13] !== props || $[14] !== t3) {
    t4 = <dialog ref={ref} onClose={onClose} className={t3} {...props}>{children}</dialog>;
    $[11] = children;
    $[12] = onClose;
    $[13] = props;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
export interface ModalHeaderProps extends ComponentPropsWithoutChildren<"div"> {
  children?: ReactNode;
  onClose?: () => void;
  closeLabel?: string;
}
export const ModalHeader: FC<ModalHeaderProps> = t0 => {
  const $ = _c(18);
  let children;
  let className;
  let onClose;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      onClose,
      closeLabel: t1,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onClose;
    $[4] = props;
    $[5] = t1;
  } else {
    children = $[1];
    className = $[2];
    onClose = $[3];
    props = $[4];
    t1 = $[5];
  }
  const closeLabel = t1 === undefined ? "Close" : t1;
  let t2;
  if ($[6] !== className) {
    t2 = cn("flex items-center justify-between border-b-[3px] border-[var(--fg-primary)] bg-[var(--bg-accent-yellow)] px-4 py-3", className);
    $[6] = className;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children) {
    t3 = <h2 className="text-lg font-bold">{children}</h2>;
    $[8] = children;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== closeLabel || $[11] !== onClose) {
    t4 = onClose && <Button type="button" variant="secondary" onClick={onClose} className="p-4"><FaTimes aria-hidden={true} className="size-[1em] shrink-0" /><span className="sr-only">{closeLabel}</span></Button>;
    $[10] = closeLabel;
    $[11] = onClose;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== props || $[14] !== t2 || $[15] !== t3 || $[16] !== t4) {
    t5 = <div className={t2} {...props}>{t3}{t4}</div>;
    $[13] = props;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
};
export interface ModalBodyProps extends ComponentPropsWithoutChildren<"div"> {
  children?: ReactNode;
}
export const ModalBody: FC<ModalBodyProps> = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("p-4 sm:p-6", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <div className={t1} {...props}>{children}</div>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export interface ModalFooterProps extends ComponentPropsWithoutChildren<"div"> {
  children?: ReactNode;
}
export const ModalFooter: FC<ModalFooterProps> = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex justify-end gap-3 border-t-2 border-black p-4", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <div className={t1} {...props}>{children}</div>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};